import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft, DollarSign, Target, TrendingUp, Briefcase, Mail, Phone, Calendar,
  Plus, Loader2, Trash2, FileText, Upload, Download, ChevronsUpDown, Check, Search,
  BarChart3, Activity, Users as UsersIcon, LineChart as LineChartIcon,
  ArrowDownToLine, ArrowUpFromLine, Coins, PieChart as PieIcon,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
  LineChart, Line, Legend, PieChart, Pie, AreaChart, Area,
} from 'recharts';
import { useAuth } from '../context/AuthContext';
import { useCompany } from '../context/CompanyContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '../components/ui/popover';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '../components/ui/command';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtCurrency = (v) =>
  v == null ? '—' : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(v);
const fmtCurrency0 = (v) =>
  v == null ? '—' : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(v);
const fmtPct = (v) => (v == null ? '—' : `${Number(v).toFixed(2)}%`);
const fmtBytes = (b) => {
  if (b == null) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};
const fmtNum = (v) => (v == null ? '—' : new Intl.NumberFormat('en-US').format(v));

function StatCard({ label, value, icon: Icon, accent = 'cyan', sublabel }) {
  const accents = {
    cyan: 'bg-cyan-50 text-cyan-600',
    green: 'bg-emerald-50 text-emerald-600',
    purple: 'bg-purple-50 text-purple-600',
    amber: 'bg-amber-50 text-amber-600',
    red: 'bg-red-50 text-red-600',
    blue: 'bg-blue-50 text-blue-600',
  };
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">{label}</p>
          <p className="text-2xl font-semibold mt-1">{value}</p>
          {sublabel && <p className="text-xs text-gray-400 mt-1">{sublabel}</p>}
        </div>
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${accents[accent]}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-xs uppercase tracking-wide text-gray-400 font-medium">{label}</dt>
      <dd className="text-sm text-gray-900">{value}</dd>
    </div>
  );
}

const emptyDeposit = { amount: '', date: new Date().toISOString().slice(0, 10), description: '' };
const emptyTrade = {
  date: new Date().toISOString().slice(0, 10),
  ticker: '', quantity: '', entry_price: '', exit_price: '', notes: '',
};

export default function PraxisClientDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { currentCompany } = useCompany();
  const isAdmin = (user || {}).role === 'admin';
  const fileInputRef = useRef(null);

  const [allClients, setAllClients] = useState([]);
  const [client, setClient] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [trades, setTrades] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const [depositOpen, setDepositOpen] = useState(false);
  const [depositForm, setDepositForm] = useState(emptyDeposit);
  const [withdrawalOpen, setWithdrawalOpen] = useState(false);
  const [withdrawalForm, setWithdrawalForm] = useState(emptyDeposit);
  const [tradeOpen, setTradeOpen] = useState(false);
  const [tradeForm, setTradeForm] = useState(emptyTrade);
  const [switcherOpen, setSwitcherOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, t, tr, f] = await Promise.all([
        axios.get(`${API_URL}/api/praxis/clients/${id}`),
        axios.get(`${API_URL}/api/praxis/clients/${id}/transactions`),
        axios.get(`${API_URL}/api/praxis/clients/${id}/trades`),
        axios.get(`${API_URL}/api/files`, { params: { praxis_client_id: id } }),
      ]);
      setClient(c.data);
      setTransactions(t.data);
      setTrades(tr.data || []);
      setFiles(f.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load client');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (!isAdmin || !currentCompany?.id) return;
    axios
      .get(`${API_URL}/api/praxis/clients`, { params: { company_id: currentCompany.id } })
      .then((res) => setAllClients(res.data || []))
      .catch(() => {});
  }, [isAdmin, currentCompany?.id]);

  useEffect(() => { load(); }, [load]);

  // --- Computed values ---
  const deposits = useMemo(() => transactions.filter((t) => t.type === 'deposit').sort((a, b) => (a.date || '').localeCompare(b.date || '')), [transactions]);
  const withdrawals = useMemo(() => transactions.filter((t) => t.type === 'withdrawal').sort((a, b) => (a.date || '').localeCompare(b.date || '')), [transactions]);
  const totalInvested = deposits.reduce((sum, t) => sum + (t.amount || 0), 0);
  const totalWithdrawn = withdrawals.reduce((sum, t) => sum + (t.amount || 0), 0);
  const intakeDate = deposits.length ? deposits[0].date : (client?.contract_start_date || null);

  const sortedTrades = useMemo(() => [...trades].sort((a, b) => (a.date || '').localeCompare(b.date || '')), [trades]);
  const totalPnL = sortedTrades.reduce((sum, t) => sum + (t.pnl || 0), 0);
  const closedTrades = sortedTrades.filter((t) => t.pnl != null);
  const winCount = closedTrades.filter((t) => (t.pnl || 0) > 0).length;
  const lossCount = closedTrades.filter((t) => (t.pnl || 0) < 0).length;
  const winRate = closedTrades.length ? (winCount / closedTrades.length) * 100 : null;

  // Cumulative PnL series
  let runningPnL = 0;
  const cumulativePnL = sortedTrades.map((t) => {
    runningPnL += t.pnl || 0;
    return { date: t.date, pnl: Number(runningPnL.toFixed(2)) };
  });

  // Capital Flow (deposits vs withdrawals) by month
  const flowByMonth = {};
  transactions.forEach((t) => {
    const k = (t.date || '').slice(0, 7) || 'unknown';
    if (!flowByMonth[k]) flowByMonth[k] = { month: k, deposits: 0, withdrawals: 0 };
    if (t.type === 'deposit') flowByMonth[k].deposits += t.amount || 0;
    else if (t.type === 'withdrawal') flowByMonth[k].withdrawals += t.amount || 0;
  });
  const capitalFlow = Object.values(flowByMonth).sort((a, b) => a.month.localeCompare(b.month));

  // PnL by ticker
  const pnlByTicker = {};
  sortedTrades.forEach((t) => {
    if (!t.ticker) return;
    pnlByTicker[t.ticker] = (pnlByTicker[t.ticker] || 0) + (t.pnl || 0);
  });
  const tickerPnL = Object.entries(pnlByTicker)
    .map(([ticker, pnl]) => ({ ticker, pnl: Number(pnl.toFixed(2)) }))
    .sort((a, b) => Math.abs(b.pnl) - Math.abs(a.pnl))
    .slice(0, 10);

  // Cumulative invested capital over time
  let runningCapital = 0;
  const cumulativeCapital = deposits.map((d) => {
    runningCapital += d.amount || 0;
    return { date: d.date, capital: Number(runningCapital.toFixed(2)), deposit: d.amount || 0 };
  });

  // Withdrawals over time (each bar = one withdrawal)
  const withdrawalSeries = withdrawals.map((w) => ({
    date: w.date,
    amount: w.amount || 0,
    description: w.description,
  }));

  // Trades P&L per trade for bar chart
  const tradesSeries = sortedTrades.map((t, i) => ({
    label: `${t.date} ${t.ticker || ''}`.trim() || `Trade ${i + 1}`,
    date: t.date,
    ticker: t.ticker,
    pnl: t.pnl || 0,
  }));

  // --- Handlers ---
  const submitTx = async (type, form, setOpen, resetForm) => {
    try {
      await axios.post(`${API_URL}/api/praxis/clients/${id}/transactions`, {
        type,
        amount: parseFloat(form.amount) || 0,
        date: form.date,
        description: form.description,
      });
      toast.success(`${type === 'deposit' ? 'Deposit' : 'Withdrawal'} recorded`);
      setOpen(false);
      resetForm();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
  };

  const submitTrade = async () => {
    if (!tradeForm.ticker.trim()) {
      toast.error('Ticker is required');
      return;
    }
    try {
      await axios.post(`${API_URL}/api/praxis/clients/${id}/trades`, {
        date: tradeForm.date,
        ticker: tradeForm.ticker,
        quantity: tradeForm.quantity,
        entry_price: tradeForm.entry_price,
        exit_price: tradeForm.exit_price,
        notes: tradeForm.notes,
      });
      toast.success('Trade added');
      setTradeOpen(false);
      setTradeForm(emptyTrade);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
  };

  const deleteTx = async (txId) => {
    if (!window.confirm('Delete this entry?')) return;
    try {
      await axios.delete(`${API_URL}/api/praxis/transactions/${txId}`);
      toast.success('Deleted');
      load();
    } catch { toast.error('Failed to delete'); }
  };

  const deleteTrade = async (tid) => {
    if (!window.confirm('Delete this trade?')) return;
    try {
      await axios.delete(`${API_URL}/api/praxis/trades/${tid}`);
      toast.success('Trade deleted');
      load();
    } catch { toast.error('Failed to delete'); }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !client) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API_URL}/api/files/upload`, fd, {
        params: { company_id: client.company_id, praxis_client_id: id },
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Document uploaded');
      const f = await axios.get(`${API_URL}/api/files`, { params: { praxis_client_id: id } });
      setFiles(f.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const deleteFile = async (fid) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await axios.delete(`${API_URL}/api/files/${fid}`);
      toast.success('Document deleted');
      setFiles((prev) => prev.filter((f) => f.id !== fid));
    } catch { toast.error('Failed to delete'); }
  };

  const downloadFile = (file) => {
    const token = localStorage.getItem('auth_token');
    const url = `${API_URL}/api/files/${file.id}/download?auth=${encodeURIComponent(token || '')}`;
    window.open(url, '_blank');
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;
  if (!client) return <div className="text-center text-gray-500 py-12">Client not found.</div>;

  const winLossData = [
    { name: 'Wins', value: winCount, fill: '#10B981' },
    { name: 'Losses', value: lossCount, fill: '#EF4444' },
  ].filter((x) => x.value > 0);

  return (
    <div className="space-y-6" data-testid="praxis-client-detail">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3">
          {isAdmin && (
            <Button variant="ghost" size="icon" onClick={() => navigate('/praxis/clients')} data-testid="back-to-clients">
              <ArrowLeft className="w-4 h-4" />
            </Button>
          )}
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">{client.client_name}</h1>
            <div className="flex flex-wrap items-center gap-3 mt-1 text-sm text-gray-500">
              {client.email && <span className="flex items-center gap-1"><Mail className="w-3.5 h-3.5" />{client.email}</span>}
              {client.phone && <span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5" />{client.phone}</span>}
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                client.status === 'active' ? 'bg-emerald-100 text-emerald-700'
                : client.status === 'closed' ? 'bg-red-100 text-red-700'
                : 'bg-gray-100 text-gray-700'
              }`}>{client.status}</span>
              {intakeDate && <span className="text-xs">Intake: <span className="font-medium text-gray-700">{intakeDate}</span></span>}
            </div>
          </div>
        </div>

        {isAdmin && allClients.length > 0 && (
          <div className="flex items-center gap-2" data-testid="client-switcher-wrapper">
            <Popover open={switcherOpen} onOpenChange={setSwitcherOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" role="combobox" aria-expanded={switcherOpen} className="w-[280px] justify-between" data-testid="client-switcher">
                  <span className="flex items-center gap-2 truncate">
                    <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <span className="truncate">{client.client_name}</span>
                  </span>
                  <ChevronsUpDown className="w-4 h-4 text-gray-400 flex-shrink-0 ml-2" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[280px] p-0" align="end">
                <Command>
                  <CommandInput placeholder="Search clients..." data-testid="client-switcher-input" />
                  <CommandList>
                    <CommandEmpty>No client found.</CommandEmpty>
                    <CommandGroup>
                      {allClients.map((c) => (
                        <CommandItem
                          key={c.id}
                          value={`${c.client_name} ${c.email || ''}`}
                          onSelect={() => {
                            setSwitcherOpen(false);
                            if (c.id !== id) navigate(`/praxis/clients/${c.id}`);
                          }}
                          data-testid={`switcher-option-${c.id}`}
                          className="cursor-pointer"
                        >
                          <Check className={`mr-2 h-4 w-4 ${c.id === id ? 'opacity-100' : 'opacity-0'}`} />
                          <div className="flex flex-col">
                            <span>{c.client_name}</span>
                            {c.email && <span className="text-xs text-gray-500">{c.email}</span>}
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
        )}
      </div>

      {/* Top Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Invested Capital" value={fmtCurrency0(totalInvested)} sublabel={intakeDate ? `since ${intakeDate}` : null} icon={Coins} accent="green" />
        <StatCard label="Withdrawn" value={fmtCurrency0(totalWithdrawn)} icon={ArrowUpFromLine} accent="red" />
        <StatCard label="Trade P&amp;L" value={fmtCurrency0(totalPnL)} sublabel={winRate != null ? `${winRate.toFixed(0)}% win rate` : null} icon={TrendingUp} accent={totalPnL >= 0 ? 'purple' : 'red'} />
        <StatCard label="AUM" value={fmtCurrency0(client.aum)} sublabel={`Target return ${fmtPct(client.target_return)}`} icon={DollarSign} accent="cyan" />
      </div>

      {/* Charts - matching Praxis Dashboard layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="p-4 border-b border-gray-100 flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-cyan-600" />
            <h2 className="font-medium text-gray-900">P&amp;L by Ticker (Top {Math.min(10, tickerPnL.length)})</h2>
          </div>
          <div className="p-4">
            {tickerPnL.length === 0 ? (
              <div className="h-[300px] flex items-center justify-center text-sm text-gray-400">No trades logged yet.</div>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={tickerPnL} margin={{ top: 5, right: 16, left: 0, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="ticker" tick={{ fontSize: 11 }} angle={-30} textAnchor="end" interval={0} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${(v / 1000).toFixed(1)}k`} />
                  <Tooltip formatter={(v) => fmtCurrency(v)} />
                  <Bar dataKey="pnl" name="P&L" radius={[4, 4, 0, 0]}>
                    {tickerPnL.map((d, i) => <Cell key={i} fill={d.pnl >= 0 ? '#06B6D4' : '#EF4444'} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="p-4 border-b border-gray-100 flex items-center gap-2">
            <PieIcon className="w-4 h-4 text-cyan-600" />
            <h2 className="font-medium text-gray-900">Win / Loss</h2>
          </div>
          <div className="p-4">
            {winLossData.length === 0 ? (
              <div className="h-[300px] flex items-center justify-center text-sm text-gray-400">No closed trades.</div>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie data={winLossData} cx="50%" cy="50%" outerRadius={90} dataKey="value" label={(d) => d.value}>
                    {winLossData.map((d, i) => <Cell key={i} fill={d.fill} />)}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      {/* Returns vs Target - full-width like Praxis dashboard */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="p-4 border-b border-gray-100 flex items-center gap-2">
          <Target className="w-4 h-4 text-cyan-600" />
          <h2 className="font-medium text-gray-900">Returns vs Target</h2>
        </div>
        <div className="p-4">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart
              data={[{ name: client.client_name, current_return: Number(client.current_return || 0), target_return: Number(client.target_return || 0) }]}
              margin={{ top: 5, right: 16, left: 0, bottom: 30 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}%`} />
              <Tooltip formatter={(v) => `${v}%`} />
              <Legend />
              <Bar dataKey="current_return" name="Current" fill="#06B6D4" radius={[4, 4, 0, 0]} />
              <Bar dataKey="target_return" name="Target" fill="#A1A1AA" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Cumulative P&L + Capital Flow */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="p-4 border-b border-gray-100 flex items-center gap-2">
            <LineChartIcon className="w-4 h-4 text-cyan-600" />
            <h2 className="font-medium text-gray-900">Cumulative P&amp;L</h2>
          </div>
          <div className="p-4">
            {cumulativePnL.length === 0 ? (
              <div className="h-[260px] flex items-center justify-center text-sm text-gray-400">No trades logged yet.</div>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={cumulativePnL} margin={{ top: 5, right: 16, left: 0, bottom: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${(v / 1000).toFixed(1)}k`} />
                  <Tooltip formatter={(v) => fmtCurrency(v)} />
                  <Line type="monotone" dataKey="pnl" stroke="#06B6D4" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="p-4 border-b border-gray-100 flex items-center gap-2">
            <Activity className="w-4 h-4 text-cyan-600" />
            <h2 className="font-medium text-gray-900">Capital Flow (Monthly)</h2>
          </div>
          <div className="p-4">
            {capitalFlow.length === 0 ? (
              <div className="h-[260px] flex items-center justify-center text-sm text-gray-400">No deposits or withdrawals yet.</div>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={capitalFlow} margin={{ top: 5, right: 16, left: 0, bottom: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v) => fmtCurrency(v)} />
                  <Legend />
                  <Bar dataKey="deposits" name="Deposits" fill="#10B981" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="withdrawals" name="Withdrawals" fill="#EF4444" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      {/* Invested Capital (Deposits) */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm" data-testid="invested-capital-section">
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h2 className="font-medium text-gray-900 flex items-center gap-2"><ArrowDownToLine className="w-4 h-4 text-emerald-600" /> Invested Capital</h2>
            <p className="text-xs text-gray-500 mt-0.5">Total invested: <span className="font-medium text-gray-700">{fmtCurrency(totalInvested)}</span>{intakeDate ? <> · Intake date: <span className="font-medium text-gray-700">{intakeDate}</span></> : null}</p>
          </div>
          {isAdmin && (
            <Button onClick={() => { setDepositForm(emptyDeposit); setDepositOpen(true); }} className="bg-emerald-600 hover:bg-emerald-700" size="sm" data-testid="add-deposit-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Deposit
            </Button>
          )}
        </div>
        {/* Cumulative Invested Capital chart */}
        <div className="p-4 border-b border-gray-100 bg-emerald-50/30">
          {cumulativeCapital.length === 0 ? (
            <div className="h-[200px] flex flex-col items-center justify-center text-sm text-gray-400">
              <ArrowDownToLine className="w-8 h-8 text-emerald-200 mb-2" />
              No capital deposits yet. Add the intake deposit to start the chart.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={cumulativeCapital} margin={{ top: 5, right: 16, left: 0, bottom: 20 }}>
                <defs>
                  <linearGradient id="invCap" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10B981" stopOpacity={0.6} />
                    <stop offset="95%" stopColor="#10B981" stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(v, n) => [fmtCurrency(v), n === 'capital' ? 'Cumulative Capital' : 'This Deposit']} />
                <Area type="monotone" dataKey="capital" stroke="#059669" strokeWidth={2} fill="url(#invCap)" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
        {deposits.length === 0 ? (
          <div className="p-12 text-center text-sm text-gray-400">No capital invested yet.</div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Notes</th>
                {isAdmin && <th className="px-4 py-3 w-12"></th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {deposits.map((t) => (
                <tr key={t.id} className="hover:bg-gray-50" data-testid={`deposit-row-${t.id}`}>
                  <td className="px-4 py-2.5 text-sm text-gray-700">{t.date}</td>
                  <td className="px-4 py-2.5 text-sm text-right font-medium text-emerald-700">{fmtCurrency(t.amount)}</td>
                  <td className="px-4 py-2.5 text-sm text-gray-600">{t.description || '—'}</td>
                  {isAdmin && (
                    <td className="px-4 py-2.5">
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600 hover:text-red-700" onClick={() => deleteTx(t.id)}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Withdrawals */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm" data-testid="withdrawals-section">
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h2 className="font-medium text-gray-900 flex items-center gap-2"><ArrowUpFromLine className="w-4 h-4 text-red-600" /> Withdrawals</h2>
            <p className="text-xs text-gray-500 mt-0.5">Total withdrawn: <span className="font-medium text-gray-700">{fmtCurrency(totalWithdrawn)}</span></p>
          </div>
          {isAdmin && (
            <Button onClick={() => { setWithdrawalForm(emptyDeposit); setWithdrawalOpen(true); }} className="bg-red-600 hover:bg-red-700" size="sm" data-testid="add-withdrawal-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Withdrawal
            </Button>
          )}
        </div>
        {/* Withdrawals chart */}
        <div className="p-4 border-b border-gray-100 bg-red-50/30">
          {withdrawalSeries.length === 0 ? (
            <div className="h-[200px] flex flex-col items-center justify-center text-sm text-gray-400">
              <ArrowUpFromLine className="w-8 h-8 text-red-200 mb-2" />
              No withdrawals yet.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={withdrawalSeries} margin={{ top: 5, right: 16, left: 0, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(v) => fmtCurrency(v)} />
                <Bar dataKey="amount" name="Withdrawal" fill="#EF4444" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
        {withdrawals.length === 0 ? (
          <div className="p-12 text-center text-sm text-gray-400">No withdrawals yet.</div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Notes</th>
                {isAdmin && <th className="px-4 py-3 w-12"></th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {withdrawals.map((t) => (
                <tr key={t.id} className="hover:bg-gray-50" data-testid={`withdrawal-row-${t.id}`}>
                  <td className="px-4 py-2.5 text-sm text-gray-700">{t.date}</td>
                  <td className="px-4 py-2.5 text-sm text-right font-medium text-red-700">{fmtCurrency(t.amount)}</td>
                  <td className="px-4 py-2.5 text-sm text-gray-600">{t.description || '—'}</td>
                  {isAdmin && (
                    <td className="px-4 py-2.5">
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600 hover:text-red-700" onClick={() => deleteTx(t.id)}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Trade Log */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm" data-testid="trade-log-section">
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h2 className="font-medium text-gray-900 flex items-center gap-2"><BarChart3 className="w-4 h-4 text-cyan-600" /> Trade Log</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {trades.length} trade{trades.length === 1 ? '' : 's'} · Total P&amp;L: <span className={`font-medium ${totalPnL >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{fmtCurrency(totalPnL)}</span>
            </p>
          </div>
          {isAdmin && (
            <Button onClick={() => { setTradeForm(emptyTrade); setTradeOpen(true); }} className="bg-cyan-600 hover:bg-cyan-700" size="sm" data-testid="add-trade-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Trade
            </Button>
          )}
        </div>
        {/* Trade P&L chart */}
        <div className="p-4 border-b border-gray-100 bg-cyan-50/30">
          {tradesSeries.length === 0 ? (
            <div className="h-[200px] flex flex-col items-center justify-center text-sm text-gray-400">
              <BarChart3 className="w-8 h-8 text-cyan-200 mb-2" />
              No trades logged yet. Log your first trade to see P&amp;L bars.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={tradesSeries} margin={{ top: 5, right: 16, left: 0, bottom: 30 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="label" tick={{ fontSize: 10 }} angle={-30} textAnchor="end" interval={0} height={60} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${(v / 1000).toFixed(1)}k`} />
                <Tooltip formatter={(v) => fmtCurrency(v)} />
                <Bar dataKey="pnl" name="P&L" radius={[4, 4, 0, 0]}>
                  {tradesSeries.map((d, i) => <Cell key={i} fill={d.pnl >= 0 ? '#10B981' : '#EF4444'} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
        {trades.length === 0 ? (
          <div className="p-12 text-center text-sm text-gray-400">No trades logged yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ticker</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Quantity</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Entry Price</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Exit Price</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">P&amp;L</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Notes</th>
                  {isAdmin && <th className="px-4 py-3 w-12"></th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {[...trades].sort((a, b) => (b.date || '').localeCompare(a.date || '')).map((t) => (
                  <tr key={t.id} className="hover:bg-gray-50" data-testid={`trade-row-${t.id}`}>
                    <td className="px-4 py-2.5 text-sm text-gray-700">{t.date}</td>
                    <td className="px-4 py-2.5 text-sm font-mono font-medium text-cyan-700">{t.ticker}</td>
                    <td className="px-4 py-2.5 text-sm text-right text-gray-700">{fmtNum(t.quantity)}</td>
                    <td className="px-4 py-2.5 text-sm text-right text-gray-700">{fmtCurrency(t.entry_price)}</td>
                    <td className="px-4 py-2.5 text-sm text-right text-gray-700">{t.exit_price == null ? <span className="text-xs text-gray-400 italic">open</span> : fmtCurrency(t.exit_price)}</td>
                    <td className={`px-4 py-2.5 text-sm text-right font-semibold ${t.pnl == null ? 'text-gray-400' : t.pnl >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{t.pnl == null ? '—' : fmtCurrency(t.pnl)}</td>
                    <td className="px-4 py-2.5 text-sm text-gray-600 max-w-[200px] truncate" title={t.notes || ''}>{t.notes || '—'}</td>
                    {isAdmin && (
                      <td className="px-4 py-2.5">
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600 hover:text-red-700" onClick={() => deleteTrade(t.id)}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Contract + Notes */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="p-4 border-b border-gray-100">
            <h2 className="font-medium text-gray-900 flex items-center gap-2"><Briefcase className="w-4 h-4 text-cyan-600" /> Contract</h2>
          </div>
          <dl className="p-4 space-y-3 text-sm">
            <Row label="Contract Amount" value={fmtCurrency(client.contract_amount)} />
            <Row label="Contract Fee" value={fmtCurrency(client.contract_fee)} />
            <Row label="Current Return" value={fmtPct(client.current_return)} />
            <Row label="Target Return" value={fmtPct(client.target_return)} />
            <Row label="Payment Schedule" value={<span className="capitalize">{client.payment_schedule || '—'}</span>} />
            <Row label="Start Date" value={client.contract_start_date || '—'} />
            <Row label="End Date" value={client.contract_end_date || '—'} />
          </dl>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="p-4 border-b border-gray-100">
            <h2 className="font-medium text-gray-900 flex items-center gap-2"><FileText className="w-4 h-4 text-cyan-600" /> Notes</h2>
          </div>
          <div className="p-4 text-sm text-gray-700 whitespace-pre-wrap min-h-[140px]">
            {client.notes || <span className="text-gray-400 italic">No notes yet.</span>}
          </div>
        </div>
      </div>

      {/* Documents */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm" data-testid="praxis-client-documents">
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h2 className="font-medium text-gray-900 flex items-center gap-2"><FileText className="w-4 h-4 text-cyan-600" /> Documents</h2>
            <p className="text-xs text-gray-500 mt-0.5">Statements, contracts, and supporting files for this client.</p>
          </div>
          {isAdmin && (
            <div>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                onChange={handleFileSelect}
                accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.csv,.xls,.xlsx"
                data-testid="praxis-file-input"
              />
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="bg-cyan-600 hover:bg-cyan-700"
                size="sm"
                data-testid="upload-doc-btn"
              >
                {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                Upload Document
              </Button>
            </div>
          )}
        </div>
        {files.length === 0 ? (
          <div className="p-12 text-center">
            <FileText className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-400">No documents uploaded yet.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {files.map((f) => (
              <div key={f.id} className="flex items-center justify-between px-4 py-3 hover:bg-gray-50" data-testid={`doc-row-${f.id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-lg bg-cyan-50 text-cyan-600 flex items-center justify-center flex-shrink-0">
                    <FileText className="w-4 h-4" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{f.filename}</p>
                    <p className="text-xs text-gray-500">{fmtBytes(f.size)} · {(f.created_at || '').slice(0, 10)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-600" onClick={() => downloadFile(f)} data-testid={`download-doc-${f.id}`}>
                    <Download className="w-4 h-4" />
                  </Button>
                  {isAdmin && (
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600 hover:text-red-700" onClick={() => deleteFile(f.id)} data-testid={`delete-doc-${f.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer link */}
      {isAdmin && (
        <div className="flex items-center justify-between pt-2">
          <Link to="/praxis/clients" className="text-sm text-cyan-600 hover:text-cyan-700 flex items-center gap-1">
            <UsersIcon className="w-4 h-4" /> All clients
          </Link>
        </div>
      )}

      {/* Add Deposit Dialog */}
      <Dialog open={depositOpen} onOpenChange={setDepositOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Deposit</DialogTitle>
            <DialogDescription>Record a capital deposit from the client.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label>Amount ($)</Label>
              <Input type="number" step="0.01" value={depositForm.amount} onChange={(e) => setDepositForm((p) => ({ ...p, amount: e.target.value }))} data-testid="deposit-amount-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Date</Label>
              <Input type="date" value={depositForm.date} onChange={(e) => setDepositForm((p) => ({ ...p, date: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label>Notes</Label>
              <Input value={depositForm.description} onChange={(e) => setDepositForm((p) => ({ ...p, description: e.target.value }))} placeholder="Optional" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDepositOpen(false)}>Cancel</Button>
            <Button onClick={() => submitTx('deposit', depositForm, setDepositOpen, () => setDepositForm(emptyDeposit))} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-deposit-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Withdrawal Dialog */}
      <Dialog open={withdrawalOpen} onOpenChange={setWithdrawalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Withdrawal</DialogTitle>
            <DialogDescription>Record a capital withdrawal to the client.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label>Amount ($)</Label>
              <Input type="number" step="0.01" value={withdrawalForm.amount} onChange={(e) => setWithdrawalForm((p) => ({ ...p, amount: e.target.value }))} data-testid="withdrawal-amount-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Date</Label>
              <Input type="date" value={withdrawalForm.date} onChange={(e) => setWithdrawalForm((p) => ({ ...p, date: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label>Notes</Label>
              <Input value={withdrawalForm.description} onChange={(e) => setWithdrawalForm((p) => ({ ...p, description: e.target.value }))} placeholder="Optional" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWithdrawalOpen(false)}>Cancel</Button>
            <Button onClick={() => submitTx('withdrawal', withdrawalForm, setWithdrawalOpen, () => setWithdrawalForm(emptyDeposit))} className="bg-red-600 hover:bg-red-700" data-testid="save-withdrawal-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Trade Dialog */}
      <Dialog open={tradeOpen} onOpenChange={setTradeOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Trade</DialogTitle>
            <DialogDescription>Log a trade. Leave Exit Price blank for an open position; P&amp;L auto-calculates from entry × quantity when exit is provided.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            <div className="space-y-1.5">
              <Label>Date</Label>
              <Input type="date" value={tradeForm.date} onChange={(e) => setTradeForm((p) => ({ ...p, date: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label>Ticker *</Label>
              <Input value={tradeForm.ticker} onChange={(e) => setTradeForm((p) => ({ ...p, ticker: e.target.value.toUpperCase() }))} placeholder="FCHL" data-testid="trade-ticker-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Quantity</Label>
              <Input type="number" step="any" value={tradeForm.quantity} onChange={(e) => setTradeForm((p) => ({ ...p, quantity: e.target.value }))} placeholder="100" />
            </div>
            <div className="space-y-1.5">
              <Label>Entry Price ($)</Label>
              <Input type="number" step="0.0001" value={tradeForm.entry_price} onChange={(e) => setTradeForm((p) => ({ ...p, entry_price: e.target.value }))} placeholder="1.50" />
            </div>
            <div className="space-y-1.5">
              <Label>Exit Price ($)</Label>
              <Input type="number" step="0.0001" value={tradeForm.exit_price} onChange={(e) => setTradeForm((p) => ({ ...p, exit_price: e.target.value }))} placeholder="2.00 (optional)" />
            </div>
            <div className="space-y-1.5">
              <Label>P&amp;L (override, $)</Label>
              <Input type="number" step="0.01" value={tradeForm.pnl || ''} onChange={(e) => setTradeForm((p) => ({ ...p, pnl: e.target.value }))} placeholder="auto" />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Notes</Label>
              <Input value={tradeForm.notes} onChange={(e) => setTradeForm((p) => ({ ...p, notes: e.target.value }))} placeholder="Optional" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTradeOpen(false)}>Cancel</Button>
            <Button onClick={submitTrade} className="bg-cyan-600 hover:bg-cyan-700" data-testid="save-trade-btn">Save Trade</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
